import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Discount value cell (Story 1.5, Design Spec §4.6).
 *
 * Presentational ("dumb") cell that renders a SINGLE discount value as `%` only —
 * used for the Current, Previous, Requested, Agreed, New and Confirmed columns. It
 * NEVER renders a `$`, list price, unit price or derived case price (BR-07).
 *
 * - Renders one line, e.g. `12.5%`.
 * - A `null` value (no previous discount, or `price_data_missing`) renders an
 *   em-dash `—` — never `0%`.
 *
 * Pure @Input() → render. No events, no CrudService/HTTP, no route.
 * Consumed inside DevExtreme cell templates.
 */
@Component({
  selector: 'app-discount-value-cell',
  templateUrl: './discount-value-cell.component.html',
  styleUrls: ['../pricing-status.css', './discount-value-cell.component.css'],
  imports: [CommonModule],
  standalone: true,
})
export class DiscountValueCellComponent {
  /** The discount percentage, or null when unavailable (renders `—`). */
  @Input() tier1Value: number | null = null;
  /** Optional discount label for the aria-label. */
  @Input() tier1Name = '';
  /** Column context for the aria-label, e.g. 'Current' / 'Previous'. */
  @Input() valueContext = '';
  /** Decimal places for the `%` value (column precision). */
  @Input() precision = 1;

  /** EM DASH for null values — never `0%`. */
  readonly EM_DASH = '—';

  /**
   * Format a discount value as `%` to the column precision; null ⇒ `—`.
   * A non-finite value (NaN/Infinity — bad data) also renders `—`, never
   * `NaN%`: the DTO emits a finite number or null.
   */
  format(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return this.EM_DASH;
    }
    return `${value.toFixed(this.precision)}%`;
  }

  /** Spoken form of a value for aria-labels: "12.5 percent" or "not available". */
  private spoken(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'not available';
    }
    return `${value.toFixed(this.precision)} percent`;
  }

  /** Build the §7 aria-label, e.g. "Current Discount 12.5 percent". */
  get ariaLabel(): string {
    const ctx = this.valueContext ? `${this.valueContext} ` : '';
    const tierName = this.tier1Name ? `${this.tier1Name} ` : '';
    return `${ctx}${tierName}Discount ${this.spoken(this.tier1Value)}`;
  }
}
